import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect, useRef, MutableRefObject } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import keys from 'common/react-query/data-keys';
import { ProjectLineItemDTO, ProjectLineItemsAndSummary } from '../../../project/project.type';
import { getBudgetandLineItems, globalUpdateProjectData } from '../../../project/project.api';
import { mapDrawsToDTO, mapLineItemsToCreateDrawLineItemDTO } from '../../../project/project.util';
import { withPrimaryDrawMirrors } from '../utils/draw-mirrors.util';
import { ReportPhotoDTO, UploadingPhoto } from '../photos/types';
import { MonitoringReportState, MonitoringReportAPIAdapter } from '../types';
import { PdfImageQuality } from '../utils/pdfCompression';
import { SaveOptions } from '../types/monitoring-report.types';
import { isReportChangedError, REPORT_CHANGED_MESSAGE } from '../utils/reportChangedError';

const REPORT_CHANGED_TOAST_ID = 'report-changed';

interface SavableDraw {
  drawId: string;
  drawSummary: ProjectLineItemsAndSummary['projectDrawSummary'];
  budgetSummary: ProjectLineItemsAndSummary['budgetSummary'];
  lineItems?: ProjectLineItemDTO[];
}

const toDrawList = (state: any): SavableDraw[] => {
  if (state?.draws?.length) return state.draws;
  if (!state?.drawSummary) return [];
  return [{ drawId: state.drawSummary.id, drawSummary: state.drawSummary, budgetSummary: state.budgetSummary, lineItems: state.lineItems }];
};

/**
 * Generic monitoring report context type
 * TPhoto: Photo type (must extend ReportPhotoDTO)
 * TExtensions: Report-specific extensions
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export interface MonitoringReportContextType<TPhoto extends ReportPhotoDTO = ReportPhotoDTO, TExtensions = Record<string, never>> {
  reportState: MonitoringReportState<TPhoto, TExtensions>;
  setReportState: React.Dispatch<React.SetStateAction<MonitoringReportState<TPhoto, TExtensions>>>;
  initialState: MonitoringReportState<TPhoto, TExtensions>;
  isDirty: boolean;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
  saveReport: (options?: SaveOptions) => Promise<void>;
  isSaving: boolean;
  // Report type
  reportType: 'limited' | 'magic';
  // Photo-related state and methods
  hasUnsavedPhotoChanges: boolean;
  setHasUnsavedPhotoChanges: (hasChanges: boolean) => void;
  registerPhotoSaveFunction: (saveFunction: () => Promise<void>) => void;
  photoSaveFunction: (() => Promise<void>) | null;
  // Photos management
  photos: TPhoto[];
  photoUrls: Record<string, string>;
  photosLoaded: boolean;
  setPhotos: React.Dispatch<React.SetStateAction<TPhoto[]>>;
  setPhotoUrls: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setPhotosLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  loadPhotos: () => Promise<void>;
  // Uploading photos management
  uploadingPhotos: UploadingPhoto[];
  setUploadingPhotos: React.Dispatch<React.SetStateAction<UploadingPhoto[]>>;
  clearUploadingPhotos: () => void;
  // PDF preview methods
  generatePDFPreview: (onProgress?: (message: string) => void) => Promise<void>;
  downloadPDF: () => void;
  /** Re-renders the previewed document at the given quality and downloads it. Resolves to the delivered size in bytes. */
  downloadPDFAtQuality: (quality: PdfImageQuality) => Promise<number | null>;
  /** Records the HTML behind the current preview so Export can re-render it at another quality. */
  setExportHtml: (html: string) => void;
  clearPDFPreview: () => void;
  // Completion status refresh trigger
  completionStatusVersion: number;
  refreshCompletionStatus: () => void;
  api: MonitoringReportAPIAdapter<TPhoto>;
  // Navigation guard — InvoiceStep registers a function here; wizard calls it before leaving the step
  navigationGuardRef: MutableRefObject<(() => Promise<boolean>) | null>;
  /** Drops unsaved changes without saving — e.g. after the report is deleted, so nothing tries to save it on the way out. */
  discardChanges: () => void;
}

/**
 * Create a generic monitoring report context
 * This factory function creates a context for any report type
 */
export function createMonitoringReportContext<TPhoto extends ReportPhotoDTO = ReportPhotoDTO, TExtensions = Record<string, never>>() {
  return createContext<MonitoringReportContextType<TPhoto, TExtensions> | undefined>(undefined);
}

/**
 * Props for the generic monitoring report provider
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export interface MonitoringReportProviderProps<TPhoto extends ReportPhotoDTO = ReportPhotoDTO, TExtensions = Record<string, never>> {
  children: ReactNode;
  initialData: MonitoringReportState<TPhoto, TExtensions>;
  projectId: string;
  reportId: string;
  api: MonitoringReportAPIAdapter<TPhoto>;
  context: React.Context<MonitoringReportContextType<TPhoto, TExtensions> | undefined>;
  reportFieldName?: string; // e.g., 'limitedReportId', 'magicReportId', etc. - for invoice DTOs
  reportType: 'limited' | 'magic'; // Type of report (limited or magic)
}

/**
 * Generic monitoring report provider component
 * Handles all common report functionality with dependency injection for report-specific logic
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export function MonitoringReportProvider<TPhoto extends ReportPhotoDTO = ReportPhotoDTO, TExtensions = Record<string, never>>({
  children,
  initialData,
  projectId,
  reportId,
  api,
  context: ReportContext,
  reportFieldName = 'reportId', // Default to generic 'reportId'
  reportType,
}: MonitoringReportProviderProps<TPhoto, TExtensions>) {
  const [reportState, setReportState] = useState<MonitoringReportState<TPhoto, TExtensions>>({
    ...initialData, // Preserve all fields from initialData (including extensions)
    reportId,
    pdfPreview: initialData.pdfPreview || { isGenerating: false },
    photos: initialData.photos || [],
    photoUrls: initialData.photoUrls || {},
    photosLoaded: initialData.photosLoaded || false,
    uploadingPhotos: initialData.uploadingPhotos || [],
    coverPagePhotoIds: initialData.coverPagePhotoIds || [],
  });

  const [initialState, setInitialState] = useState<MonitoringReportState<TPhoto, TExtensions>>({
    ...initialData, // Preserve all fields from initialData (including extensions)
    reportId,
    pdfPreview: initialData.pdfPreview || { isGenerating: false },
    photos: initialData.photos || [],
    photoUrls: initialData.photoUrls || {},
    photosLoaded: initialData.photosLoaded || false,
    uploadingPhotos: initialData.uploadingPhotos || [],
    coverPagePhotoIds: initialData.coverPagePhotoIds || [],
  });

  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const inFlightSaveRef = useRef<Promise<void> | null>(null);
  // Set once the backend refuses a save as stale (409 report_changed). From then on nothing is
  // saved from this tab — it would overwrite someone else's newer work — until the page reloads.
  const reportChangedRef = useRef(false);

  // Photo-related state
  const [hasUnsavedPhotoChanges, setHasUnsavedPhotoChanges] = useState<boolean>(false);
  const [photoSaveFunction, setPhotoSaveFunction] = useState<(() => Promise<void>) | null>(null);
  const flushPendingUpdatesRef = useRef<(() => void) | null>(null);
  const navigationGuardRef = useRef<(() => Promise<boolean>) | null>(null);
  // HTML behind the current preview, so Export can re-render the exact same
  // document at a different quality -- including any edits made in the editor.
  // A ref, not state: it is a multi-megabyte string that nothing renders.
  const lastExportHtmlRef = useRef<string | null>(null);

  // Refs to track latest state values (for accessing after flush)
  const reportStateRef = useRef(reportState);
  const isDirtyRef = useRef(isDirty);
  const hasUnsavedPhotoChangesRef = useRef(hasUnsavedPhotoChanges);

  // Photos state - separate from reportState for easier management
  const [photos, setPhotos] = useState<TPhoto[]>([]);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const [photosLoaded, setPhotosLoaded] = useState<boolean>(false);

  // Uploading photos state
  const [uploadingPhotos, setUploadingPhotos] = useState<UploadingPhoto[]>([]);

  // Completion status refresh trigger
  const [completionStatusVersion, setCompletionStatusVersion] = useState<number>(0);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /**
   * The state every consumer sees, with the primary-draw mirrors derived from `draws`.
   * Nothing outside this provider should read the raw `reportState`.
   */
  const derivedReportState = React.useMemo(() => withPrimaryDrawMirrors(reportState as any) as typeof reportState, [reportState]);

  // Keep refs in sync with state (derived, so saving/exporting sees consistent mirrors)
  useEffect(() => {
    reportStateRef.current = derivedReportState;
  }, [derivedReportState]);

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    hasUnsavedPhotoChangesRef.current = hasUnsavedPhotoChanges;
  }, [hasUnsavedPhotoChanges]);

  // Cleanup uploading photos on unmount
  useEffect(() => {
    return () => {
      setUploadingPhotos([]);
    };
  }, []);

  // Sync updated author information from parent props
  useEffect(() => {
    if (initialData.author && initialData.author.signatureUrl !== reportState.author?.signatureUrl) {
      setReportState(prev => ({ ...prev, author: initialData.author }));
      setInitialState(prev => ({ ...prev, author: initialData.author }));
    }
  }, [initialData.author, reportState.author?.signatureUrl]);

  const registerPhotoSaveFunction = useCallback((saveFunction: () => Promise<void>) => {
    setPhotoSaveFunction(() => saveFunction);
  }, []);

  const refreshCompletionStatus = useCallback(() => {
    setCompletionStatusVersion(prev => prev + 1);
  }, []);

  const clearUploadingPhotos = useCallback(() => {
    setUploadingPhotos([]);
  }, []);

  const loadPhotos = useCallback(async () => {
    if (photosLoaded) return; // Don't reload if already loaded

    try {
      const loadedPhotos = await api.getPhotos(reportId);
      setPhotos(loadedPhotos);

      // Load thumbnail URLs
      const urlPromises = loadedPhotos.map(async photo => {
        try {
          const url = await api.getPhotoUrl(photo.thumbnailBlobName || photo.blobName);
          return { id: photo.id, url };
        } catch (error) {
          console.error(`Failed to load thumbnail for photo ${photo.id}:`, error);
          return { id: photo.id, url: '' };
        }
      });

      const urlResults = await Promise.all(urlPromises);
      const urlMap: Record<string, string> = {};
      urlResults.forEach(({ id, url }) => {
        urlMap[id] = url;
      });
      setPhotoUrls(urlMap);
      setPhotosLoaded(true);
    } catch (error) {
      console.error('Error loading photos:', error);
      toast.error('Failed to load photos');
    }
  }, [reportId, photosLoaded, api]);

  // Load photos on context initialization
  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const discardChanges = useCallback(() => {
    isDirtyRef.current = false;
    hasUnsavedPhotoChangesRef.current = false;
    setIsDirty(false);
    setHasUnsavedPhotoChanges(false);
  }, []);

  const saveReport = async (options?: SaveOptions) => {
    if (reportChangedRef.current) {
      toast.error(REPORT_CHANGED_MESSAGE, { toastId: REPORT_CHANGED_TOAST_ID, autoClose: false });
      return;
    }

    // If a save is already in flight, wait for it, then re-check whether this call's
    // changes still need saving — otherwise this call's dirty state can be silently
    // consumed by the in-flight save without ever being persisted.
    while (inFlightSaveRef.current) {
      await inFlightSaveRef.current;
      if (!isDirtyRef.current && !hasUnsavedPhotoChangesRef.current) {
        return;
      }
    }

    const runPromise = runSaveReport(options).catch(error => {
      console.error('Unexpected error during save', error);
    });
    inFlightSaveRef.current = runPromise;
    try {
      await runPromise;
    } finally {
      if (inFlightSaveRef.current === runPromise) {
        inFlightSaveRef.current = null;
      }
    }
  };

  const runSaveReport = async (options?: SaveOptions) => {
    // Flush all pending debounced updates FIRST (this will set isDirty if there are pending changes)
    if (flushPendingUpdatesRef.current) {
      flushPendingUpdatesRef.current();

      // Give React time to process all state updates and sync refs
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    // Use refs to get the latest values after flush
    const hasAnyChanges = isDirtyRef.current || hasUnsavedPhotoChangesRef.current;
    if (!hasAnyChanges) {
      if (!options?.silent) {
        toast.info('No changes to save.');
      }
      return;
    }

    setIsSaving(true);
    api.beginSave?.(reportId, { reason: options?.reason ?? 'manual', step: options?.step });

    const savedSnapshot = reportStateRef.current;
    let hasFailure = false;
    try {
      // Save photo changes first if any
      if (hasUnsavedPhotoChangesRef.current && photoSaveFunction) {
        await photoSaveFunction();
        setHasUnsavedPhotoChanges(false);
      }

      // Then save other changes if any - use ref to get latest state
      if (isDirtyRef.current) {
        const currentReportState = savedSnapshot;
        const { projectSummary, summary, invoices } = currentReportState;
        const { invoices: initialInvoices } = initialState;

        // Update summary and invoices to backend
        if (
          currentReportState.reportId &&
          (summary !== undefined ||
            (currentReportState as any).previousFundingsComment !== undefined ||
            (currentReportState as any).siteConditions !== undefined ||
            (currentReportState as any).invoiceData !== undefined)
        ) {
          try {
            // Prepare invoices for backend - handle new, updated, and deleted invoices
            const currentInvoices = invoices || [];
            const originalInvoices = initialInvoices || [];

            // Find new invoices (temp IDs)
            const newInvoices = currentInvoices.filter(invoice => invoice.id.startsWith('temp-'));

            // Find updated invoices (existing IDs that have changed)
            const updatedInvoices = currentInvoices.filter(invoice => {
              if (invoice.id.startsWith('temp-')) return false; // Skip new invoices
              const originalInvoice = originalInvoices.find(orig => orig.id === invoice.id);
              return originalInvoice && JSON.stringify(invoice) !== JSON.stringify(originalInvoice);
            });

            // Find deleted invoices (in original but not in current)
            const deletedInvoiceIds = originalInvoices
              .filter(orig => !currentInvoices.find(curr => curr.id === orig.id))
              .map(invoice => invoice.id);

            // Prepare DTOs for new and updated invoices
            // Map generic reportId to specific field name (e.g., limitedReportId)
            const invoiceDTOs = [...newInvoices, ...updatedInvoices].map(invoice => ({
              [reportFieldName]: invoice.reportId, // Map to specific field name
              invoice: {
                number: invoice.invoice.number,
                invoiceAmount: invoice.invoice.invoiceAmount,
                invoiceDate: invoice.invoice.invoiceDate,
                paidAmount: invoice.invoice.paidAmount,
              },
              recepient: invoice.recepient,
              recepientAddress: invoice.recepientAddress,
              projectName: invoice.projectName,
              projectAddress: invoice.projectAddress,
              service: invoice.service,
            }));

            // For magic reports, merge coverPagePhotoIds into coverPage
            let coverPageToSave = (currentReportState as any).coverPage;
            if (reportType === 'magic' && coverPageToSave) {
              coverPageToSave = {
                ...coverPageToSave,
                coverPagePhotoIds: currentReportState.coverPagePhotoIds || [],
              };
            }

            await api.updateReport(currentReportState.reportId, {
              name: currentReportState.projectSummary?.projectTitle || 'Report',
              summary: summary
                ? {
                    coverLetter: summary?.coverLetter,
                    executiveSummary: summary?.executiveSummary,
                    actionItems: summary?.actionItems,
                  }
                : undefined,
              previousFundingsNote: (currentReportState as any).previousFundingsComment,
              // Only include coverPage/invoiceData for magic reports
              ...(reportType === 'magic' && {
                coverPage: coverPageToSave,
                siteConditions: (currentReportState as any).siteConditions,
                invoiceData: (currentReportState as any).invoiceData,
                highlightedLineItemIds: (currentReportState as any).highlightedLineItemIds,
              }),
              invoices: invoiceDTOs,
            });
          } catch (error) {
            if (isReportChangedError(error)) throw error;
            console.error('Failed to save report data', error);
            toast.error('Failed to save report data.');
            hasFailure = true;
          }
        }

        // Save sections separately (independent of summary changes)
        if (currentReportState.reportId && (currentReportState as any).sections) {
          try {
            const sectionsToSave = (currentReportState as any).sections;
            await api.updateReport(currentReportState.reportId, {
              name: currentReportState.projectSummary?.projectTitle || 'Report',
              summary: undefined, // Only saving sections, not summary
              invoices: [],
              sections: sectionsToSave,
            });
            if (sectionsToSave !== (currentReportState as any).sections) {
              setReportState(prev => ({ ...prev, sections: sectionsToSave }));
            }
          } catch (error) {
            if (isReportChangedError(error)) throw error;
            console.error('Failed to save sections', error);
            toast.error('Failed to save sections.');
            hasFailure = true;
          }
        }

        const drawsToSave = toDrawList(currentReportState);
        const initialDraws = toDrawList(initialState);

        const updatedDraws = drawsToSave
          .filter(draw => {
            const initialDraw = initialDraws.find(d => d.drawId === draw.drawId);
            return (
              JSON.stringify(draw.drawSummary) !== JSON.stringify(initialDraw?.drawSummary) ||
              JSON.stringify(draw.budgetSummary) !== JSON.stringify(initialDraw?.budgetSummary)
            );
          })
          .map(draw => ({ projectDrawSummary: draw.drawSummary, budgetSummary: draw.budgetSummary }) as ProjectLineItemsAndSummary);
        const updateDrawDTOs = mapDrawsToDTO(updatedDraws, projectId);

        const lineItemsFor = (predicate: (item: ProjectLineItemDTO) => boolean) =>
          drawsToSave
            .map(draw => ({
              drawId: draw.drawId,
              lineItems: mapLineItemsToCreateDrawLineItemDTO(draw.drawId, (draw.lineItems || []).filter(predicate)),
            }))
            .filter(dto => dto.lineItems.length > 0);

        const createDtos = lineItemsFor(item => !!item.isCreated);
        const updateDtos = lineItemsFor(item => !!item.isEdited && !item.isCreated);
        const deleteDtos = drawsToSave
          .map(draw => ({
            drawId: draw.drawId,
            lineItemIds: (draw.lineItems || []).filter(item => item.isDeleted).map(item => item.id),
          }))
          .filter(dto => dto.lineItemIds.length > 0);

        const projectDataDTO = {
          projectSummary: null,
          createdDraws: [],
          updatedDraws: updateDrawDTOs,
          deletedDrawIds: [],
          lineItems: {
            create: createDtos,
            update: updateDtos,
            delete: deleteDtos,
          },
          auditMessage: [],
        };

        await globalUpdateProjectData(projectId, projectDataDTO);

        void Promise.all(
          [keys.project, keys.projectDraws, keys.projectLineItemsAndBudgets, keys.projectHistory].map(key =>
            queryClient.invalidateQueries([key, projectId]),
          ),
        );

        const touchedDrawIds = Array.from(new Set([...createDtos, ...updateDtos, ...deleteDtos].map(dto => dto.drawId)));
        if (touchedDrawIds.length > 0) {
          const savedLineItems = new Map(drawsToSave.map(draw => [draw.drawId, draw.lineItems]));
          const replaceSavedLineItems = (
            getNext: (drawId: string, saved: ProjectLineItemDTO[] | undefined) => ProjectLineItemDTO[] | undefined,
          ) => {
            const replace = (drawId: string, current: ProjectLineItemDTO[] | undefined) =>
              touchedDrawIds.includes(drawId) && current === savedLineItems.get(drawId) ? (getNext(drawId, current) ?? current) : current;

            setReportState((prev: any) =>
              prev.draws?.length
                ? { ...prev, draws: prev.draws.map((draw: any) => ({ ...draw, lineItems: replace(draw.drawId, draw.lineItems) })) }
                : prev.drawSummary
                  ? { ...prev, lineItems: replace(prev.drawSummary.id, prev.lineItems) }
                  : prev,
            );
          };

          try {
            const refreshed = await Promise.all(touchedDrawIds.map(drawId => getBudgetandLineItems(projectId, drawId)));
            const freshLineItems = new Map(refreshed.map((data, i) => [touchedDrawIds[i], data?.lineItems]));
            replaceSavedLineItems(drawId => freshLineItems.get(drawId));
          } catch (error) {
            console.error('Failed to reload line items after save', error);
            replaceSavedLineItems((_drawId, saved) =>
              saved?.filter(item => !item.isDeleted).map(item => ({ ...item, isCreated: false, isEdited: false })),
            );
            toast.warning(
              'Budget changes were saved, but the line items could not be reloaded. Please reopen the report before editing again.',
            );
          }
        }
      }

      setInitialState(hasFailure ? { ...savedSnapshot, invoices: initialState.invoices } : savedSnapshot);

      if (hasFailure) return;

      if (reportStateRef.current === savedSnapshot) {
        isDirtyRef.current = false;
        setIsDirty(false);
      }

      // Silent saves (completion toggles) report progress in the section's own circle instead.
      if (!options?.silent) {
        toast.success('All changes saved successfully.');
      }
      refreshCompletionStatus();
    } catch (error) {
      console.error(error);
      if (isReportChangedError(error)) {
        reportChangedRef.current = true;
        toast.error(REPORT_CHANGED_MESSAGE, { toastId: REPORT_CHANGED_TOAST_ID, autoClose: false });
      } else {
        toast.error('Failed to save changes. Your edits are still unsaved — please try again.');
      }
    } finally {
      api.endSave?.(reportId);
      setIsSaving(false);
    }
  };

  const generatePDFPreview = async (onProgress?: (message: string) => void) => {
    if (!reportState.reportId) return;

    setReportState(prev => ({
      ...prev,
      pdfPreview: { ...prev.pdfPreview, isGenerating: true, error: undefined },
    }));

    try {
      // Ensure any unsaved photo changes are persisted before generating preview
      if (hasUnsavedPhotoChanges && photoSaveFunction) {
        onProgress?.('Saving photo changes...');
        await photoSaveFunction();
        setHasUnsavedPhotoChanges(false);
      }

      if (!reportState.projectSummary) {
        toast.error('Project summary not found');
        return;
      }

      onProgress?.('Preparing HTML...');
      // Derived state, so the export sees mirrors consistent with `draws`.
      const html = await api.buildReportHtml(derivedReportState);

      onProgress?.('Generating PDF...');
      // The preview always renders uncompressed; only Export applies a preset,
      // so what the user reviews on screen is the full-quality document.
      const { blob, sizeInfo } = await api.exportPdf(html);
      lastExportHtmlRef.current = html;

      const projTitle = reportState.projectSummary?.projectTitle || 'Report';
      const sanitizeFilename = (s: string) =>
        s
          .replace(/[\\/:*?"<>|]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      const formatFilenameDate = (dateStr: string | undefined | null) => {
        if (!dateStr) return '';
        const parts = dateStr.split('T')[0].split('-');
        return parts.length === 3 ? `${parts[1]}-${parts[2]}-${parts[0].slice(-2)}` : '';
      };
      const reportDate = formatFilenameDate(reportState.siteConditions?.reportDate || reportState.siteConditions?.inspectionDate);
      const reportNum =
        reportType === 'limited' ? reportState.siteConditions?.siteVisit || '' : derivedReportState.drawSummary?.applicationNumber || '';
      const suffix = reportType === 'limited' ? ' LIMITED' : '';
      const fileName = `${sanitizeFilename(projTitle)}${reportDate ? ' ' + reportDate : ''} CDCC Report${reportNum ? ' ' + reportNum : ''}${suffix}.pdf`;
      const blobUrl = URL.createObjectURL(blob);

      setReportState(prev => ({
        ...prev,
        pdfPreview: {
          blobUrl,
          fileName,
          isGenerating: false,
          error: undefined,
          sizeInfo,
        },
      }));
    } catch (error) {
      console.error('Error generating PDF preview:', error);
      setReportState(prev => ({
        ...prev,
        pdfPreview: {
          ...prev.pdfPreview,
          isGenerating: false,
          error: error instanceof Error ? error.message : 'Failed to generate PDF preview',
        },
      }));
    }
  };

  const triggerDownload = (href: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = href;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const setExportHtml = useCallback((html: string) => {
    lastExportHtmlRef.current = html;
  }, []);

  const downloadPDF = useCallback(() => {
    if (!reportState.pdfPreview?.blobUrl || !reportState.pdfPreview?.fileName) return;
    triggerDownload(reportState.pdfPreview.blobUrl, reportState.pdfPreview.fileName);
  }, [reportState.pdfPreview]);

  /**
   * Re-renders the previewed document at `quality` and downloads it.
   * `original` short-circuits to the preview blob, which was already rendered
   * uncompressed -- no second round trip.
   * Returns the delivered size so the caller can report it.
   */
  const downloadPDFAtQuality = useCallback(
    async (quality: PdfImageQuality): Promise<number | null> => {
      const fileName = reportState.pdfPreview?.fileName;
      if (!fileName) return null;

      if (quality === 'original') {
        if (!reportState.pdfPreview?.blobUrl) return null;
        triggerDownload(reportState.pdfPreview.blobUrl, fileName);
        return reportState.pdfPreview.sizeInfo?.totalBytes ?? null;
      }

      const html = lastExportHtmlRef.current;
      if (!html) {
        // No captured HTML means no preview was generated in this session.
        throw new Error('Report preview is not available. Refresh the preview and try again.');
      }

      const { blob } = await api.exportPdf(html, true, quality);
      const url = URL.createObjectURL(blob);
      triggerDownload(url, fileName);
      // Revoking synchronously can cancel the download before the browser has
      // started reading the blob.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return blob.size;
    },
    [api, reportState.pdfPreview],
  );

  const clearPDFPreview = useCallback(() => {
    setReportState(prev => {
      if (prev.pdfPreview?.blobUrl) {
        URL.revokeObjectURL(prev.pdfPreview.blobUrl);
      }
      return {
        ...prev,
        pdfPreview: { isGenerating: false },
      };
    });
  }, []);

  const contextValue: MonitoringReportContextType<TPhoto, TExtensions> = {
    // Derived, never the raw state — see withPrimaryDrawMirrors.
    reportState: derivedReportState,
    setReportState,
    initialState,
    isDirty,
    setIsDirty,
    saveReport,
    isSaving,
    reportType,
    hasUnsavedPhotoChanges,
    setHasUnsavedPhotoChanges,
    registerPhotoSaveFunction,
    photoSaveFunction,
    photos,
    photoUrls,
    photosLoaded,
    setPhotos,
    setPhotoUrls,
    setPhotosLoaded,
    loadPhotos,
    uploadingPhotos,
    setUploadingPhotos,
    clearUploadingPhotos,
    generatePDFPreview,
    downloadPDF,
    downloadPDFAtQuality,
    setExportHtml,
    clearPDFPreview,
    completionStatusVersion,
    refreshCompletionStatus,
    api,
    navigationGuardRef,
    discardChanges,
    isDirtyRef, // Read synchronously by navigation guards (discardChanges clears it before state re-renders)
    flushPendingUpdatesRef, // Expose the ref so child components can set it
  } as any;

  return (
    <MonitoringReportSharedContext.Provider value={contextValue}>
      <ReportContext.Provider value={contextValue}>{children}</ReportContext.Provider>
    </MonitoringReportSharedContext.Provider>
  );
}

/**
 * Create a custom hook for a specific report type
 * Usage: export const useLimitedReport = createUseMonitoringReport(LimitedReportContext);
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export function createUseMonitoringReport<TPhoto extends ReportPhotoDTO = ReportPhotoDTO, TExtensions = Record<string, never>>(
  context: React.Context<MonitoringReportContextType<TPhoto, TExtensions> | undefined>,
  contextName: string,
) {
  return () => {
    const ctx = useContext(context);
    if (!ctx) {
      throw new Error(`${contextName} must be used within the corresponding Provider`);
    }
    return ctx;
  };
}

// A shared context instance for steps/components that are reused across report types
// Report-specific providers can also optionally provide this same context value to enable shared steps
export const MonitoringReportSharedContext = createContext<MonitoringReportContextType<any, any> | undefined>(undefined);

export const useMonitoringReportShared = () => {
  const ctx = useContext(MonitoringReportSharedContext);
  if (!ctx) {
    throw new Error('useMonitoringReportShared must be used within MonitoringReportSharedContext.Provider');
  }
  return ctx;
};
